import React from 'react';
import { MemoType, VisitMemo, SaveMemoRequest, Patient } from '../../types/api';
import { MemoFormState, AlertModalState } from '../../types/dispense';
import { apiService } from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import { getTodayISO } from '../../utils/date';

const MEMO_TYPES_MAP = [
  { type: MemoType.Referral, label: 'Referral', icon: '📋' },
  { type: MemoType.Memo, label: 'Memo', icon: '📝' },
  { type: MemoType.Prescription, label: 'Prescription', icon: '💊' },
  { type: MemoType.HFMD, label: 'HFMD', icon: '🤒' },
  { type: MemoType.Chickenpox, label: 'Chickenpox', icon: '🦠' },
  { type: MemoType.POCT, label: 'POCT', icon: '🧪' },
  { type: MemoType.Vaccination, label: 'Vaccination', icon: '💉' },
  { type: MemoType.HAJ, label: 'HAJ', icon: '🏥' }
];

interface MemoFormModalProps {
  show: boolean;
  editingMemo: VisitMemo | null;
  selectedMemoType: MemoType | null;
  memoForm: MemoFormState;
  patient: Patient;
  setShow: (show: boolean) => void;
  setEditingMemo: (memo: VisitMemo | null) => void;
  setSelectedMemoType: (type: MemoType | null) => void;
  setMemoForm: (form: MemoFormState) => void;
  setShowMemoTypeModal: (show: boolean) => void;
  setAlertModal: (modal: AlertModalState) => void;
  loadMemos: () => Promise<void>;
  loadCurrentOrders: () => Promise<void>;
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '14px 16px', border: '2px solid #d1d5db', borderRadius: '10px',
  fontSize: '15px', fontFamily: 'inherit', color: '#1f2937', background: '#ffffff', transition: 'all 0.2s ease'
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: 600, color: '#374151', letterSpacing: '0.3px'
};

const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  e.currentTarget.style.borderColor = '#0284c7';
  e.currentTarget.style.boxShadow = '0 0 0 4px rgba(2, 132, 199, 0.1)';
  e.currentTarget.style.background = '#f8fafc';
};

const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  e.currentTarget.style.borderColor = '#d1d5db';
  e.currentTarget.style.boxShadow = 'none';
  e.currentTarget.style.background = '#ffffff';
};

export function MemoFormModal({
  show, editingMemo, selectedMemoType, memoForm, patient,
  setShow, setEditingMemo, setSelectedMemoType, setMemoForm,
  setShowMemoTypeModal, setAlertModal, loadMemos, loadCurrentOrders
}: MemoFormModalProps) {
  if (!show) return null;

  const resetAndClose = () => {
    setShow(false);
    setEditingMemo(null);
    setSelectedMemoType(null);
    setMemoForm({ recipient: '', subHeader: '', body: '', signOff: '', memoDate: getTodayISO() });
  };

  const handleSave = async () => {
    if (!editingMemo && selectedMemoType === null) {
      setAlertModal({ show: true, message: 'Please select a memo type', type: 'error' });
      return;
    }
    try {
      const details = await apiService.getPatientDetails(patient.visitULID);
      if (!details?.customerULID) {
        setAlertModal({ show: true, message: 'Unable to get customer information', type: 'error' });
        return;
      }
      const request: SaveMemoRequest = {
        visitULID: patient.visitULID,
        memoULID: editingMemo ? editingMemo.ulid : undefined,
        customerULID: details.customerULID,
        memoType: editingMemo ? editingMemo.memoType : selectedMemoType!,
        recipient: memoForm.recipient,
        subHeader: memoForm.subHeader,
        body: memoForm.body,
        signOff: memoForm.signOff,
        memoDate: memoForm.memoDate || getTodayISO()
      };
      const response = await apiService.saveMemo(patient.visitULID, request);
      if (response.success) {
        resetAndClose();
        setAlertModal({ show: true, message: editingMemo ? 'Memo updated successfully' : 'Memo created successfully', type: 'success' });
        await loadMemos();
        await loadCurrentOrders();
      } else {
        setAlertModal({ show: true, message: response.message || 'Failed to save memo', type: 'error' });
      }
    } catch (error: any) {
      setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not save memo. Please try again.'), type: 'error' });
    }
  };

  const getMemoTypeInfo = (type: MemoType | null) => {
    return MEMO_TYPES_MAP.find(m => m.type === type) || { label: 'Memo', icon: '📄' };
  };

  return (
    <div
      className="modal-overlay"
      onClick={resetAndClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
        zIndex: 2500, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div
        className="modal modal-large"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '800px', maxHeight: '90vh', background: '#ffffff',
          borderRadius: '20px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          animation: 'slideUpModal 0.3s ease-out', position: 'relative'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '24px 28px', borderBottom: '2px solid #e5e7eb',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #bae6fd 100%)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#0c4a6e', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
              <span style={{ fontSize: '28px' }}>📄</span>
              {editingMemo ? 'Edit Memo' : 'Create New Memo'}
            </div>
            {editingMemo && editingMemo.memoTypeName && (
              <div style={{ fontSize: '14px', color: '#64748b', marginLeft: '40px' }}>
                Type: {editingMemo.memoTypeName}
              </div>
            )}
          </div>
          <button onClick={resetAndClose} style={{
            background: 'rgba(255, 255, 255, 0.8)', border: '2px solid rgba(0, 0, 0, 0.1)', borderRadius: '12px',
            width: '44px', height: '44px', fontSize: '24px', cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', color: '#64748b', fontWeight: 300
          }}>&times;</button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '28px', paddingBottom: '40px',
          background: '#fafbfc', minHeight: 0
        }}>
          {/* Show selected memo type info */}
          {!editingMemo && selectedMemoType !== null && (
            <div style={{
              background: '#ffffff', borderRadius: '16px', padding: '20px', marginBottom: '20px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '2px solid #e5e7eb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>{getMemoTypeInfo(selectedMemoType).icon}</span>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                    {getMemoTypeInfo(selectedMemoType).label}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Memo Type</div>
                </div>
              </div>
              <button
                onClick={() => {
                  setShow(false);
                  setSelectedMemoType(null);
                  setMemoForm({ recipient: '', subHeader: '', body: '', signOff: '', memoDate: getTodayISO() });
                  setShowMemoTypeModal(true);
                }}
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: 600, color: '#64748b',
                  background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '8px',
                  cursor: 'pointer', transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.borderColor = '#94a3b8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
              >
                Change Type
              </button>
            </div>
          )}

          {/* Form Fields */}
          {(editingMemo || selectedMemoType !== null) && (
            <div style={{ padding: '0' }}>
              {/* Section Header */}
              <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e5e7eb' }}>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '22px' }}>✏️</span>
                  {editingMemo ? 'Edit Memo Details' : 'Enter Memo Details'}
                </div>
                {selectedMemoType !== null && !editingMemo && (
                  <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>
                    Memo Type: {getMemoTypeInfo(selectedMemoType).label}
                  </div>
                )}
              </div>

              {/* Form Fields Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: '24px', width: '100%', boxSizing: 'border-box' }}>
                {/* To (Recipient) */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>To (Recipient)</label>
                  <input type="text" value={memoForm.recipient} onChange={(e) => setMemoForm({ ...memoForm, recipient: e.target.value })} placeholder="Enter recipient name or organization" style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                </div>

                {/* Re (Sub Header) */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Re (Subject/Sub Header)</label>
                  <input type="text" value={memoForm.subHeader} onChange={(e) => setMemoForm({ ...memoForm, subHeader: e.target.value })} placeholder="Enter subject or reference" style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                </div>

                {/* Body - Full Width */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Body <span style={{ color: '#ef4444' }}>*</span></label>
                  <textarea value={memoForm.body} onChange={(e) => setMemoForm({ ...memoForm, body: e.target.value })} rows={12} placeholder="Enter memo body content..." style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.6', minHeight: '200px' }} onFocus={handleFocus as any} onBlur={handleBlur as any} />
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>You can use multiple lines for formatting</span>
                    <span>{memoForm.body.length} characters</span>
                  </div>
                </div>

                {/* Sign Off */}
                <div style={{ gridColumn: 'span 1' }}>
                  <label style={labelStyle}>Sign Off</label>
                  <textarea rows={4} value={memoForm.signOff} onChange={(e) => setMemoForm({ ...memoForm, signOff: e.target.value })} placeholder="Doctor's sign off" style={{ ...inputStyle, resize: 'vertical', boxSizing: 'border-box' }} onFocus={handleFocus as any} onBlur={handleBlur as any} />
                </div>

                {/* Memo Date */}
                <div style={{ gridColumn: 'span 1' }}>
                  <label style={labelStyle}>Memo Date</label>
                  <input type="date" value={memoForm.memoDate} onChange={(e) => setMemoForm({ ...memoForm, memoDate: e.target.value })} style={{ ...inputStyle, cursor: 'pointer', boxSizing: 'border-box' }} onFocus={handleFocus} onBlur={handleBlur} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {(editingMemo || selectedMemoType !== null) && (
          <div style={{
            padding: '20px 28px', borderTop: '2px solid #e5e7eb', background: '#ffffff',
            display: 'flex', gap: '12px', justifyContent: 'flex-end', flexShrink: 0,
            position: 'sticky', bottom: 0, zIndex: 10, flexWrap: 'wrap'
          }}>
            <button className="btn btn-secondary" onClick={resetAndClose} style={{ padding: '14px 28px', fontSize: '15px', fontWeight: 600, borderRadius: '12px', minWidth: '100px' }}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} style={{
              padding: '14px 32px', fontSize: '16px', fontWeight: 700, borderRadius: '12px',
              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', color: '#ffffff',
              border: 'none', cursor: 'pointer', minWidth: '140px'
            }}>
              💾 {editingMemo ? 'Update Memo' : 'Save Memo'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
